import { Guid } from "@/util/guid";

import attributeDataApi from "@services/attributeData.js";
import attributeDataTypesApi from "@services/attributeDataTypes.js";
import attributeTypesApi from "@services/attributeTypes.js";
import attributesApi from "@services/attributes.js";
import catalogAttributesApi from "@services/catalogAttributes.js";
import catalogGroupAttributesApi from "@services/catalogGroupAttributes.js";
import commonApi from "@services/common.js";
import erpAttributesApi from "@services/erpAttributes.js";
import erpSectionsApi from "@services/erpSections.js";
import globalAttributesApi from "@services/globalAttributes.js";
import keywordTypesApi from "@services/keywordTypes.js";
import mediaAssetTypesApi from "@services/mediaAssetTypes.js";
import mediaAssetsApi from "@services/mediaAssets.js";
import priceAttributesApi from "@services/priceAttributes.js";
import referenceAttributesApi from "@services/referenceAttributes.js";
import referenceTypesApi from "@services/referenceTypes.js";
import gpsrApi from "@services/gpsr";
import { sha1 as hash } from "object-hash";

import _ from "lodash";

function defaultState() {
  return {
    metadata: {
      update: {
        jobs: [],
        error: "",
        apiError: false,
        success: true,
        seen: true
      }
    },
    isSaving: false,
    modified: {
      attributes: false,
      attributeTypes: false,
      globalAttributes: false,
      mediaTypes: false,
      articleMediaAssets: false,
      catalogGroupMediaAssets: false,
      referenceTypes: false,
      referenceAttributes: false,
      defaultCatalogAttributes: false,
      printCatalogAttributes: false,
      catalogGroupAttributes: false,
      priceAttributes: false,
      erpSections: false,
      erpAttributes: false,
      keywordTypes: false,
      gpsr: false
    },
    deleted: {
      attributes: [],
      priceAttributes: [],
      attributeTypes: [],
      globalAttributes: [],
      mediaTypes: [],
      articleMediaAssets: [],
      catalogGroupMediaAssets: [],
      defaultCatalogAttributes: [],
      printCatalogAttributes: [],
      catalogGroupAttributes: [],
      keywordTypes: [],
      referenceTypes: [],
      referenceAttributes: [],
      erpSections: [],
      erpAttributes: [],
      gpsr: []
    },
    errors: {
      attributes: undefined,
      attributeTypes: undefined,
      globalAttributes: undefined,
      mediaTypes: undefined,
      articleMediaAssets: undefined,
      catalogGroupMediaAssets: undefined,
      defaultCatalogAttributes: undefined,
      printCatalogAttributes: undefined,
      catalogGroupAttributes: undefined,
      referenceTypes: false,
      referenceAttributes: false,
      priceAttributes: undefined,
      erpSections: undefined,
      erpAttributes: undefined,
      keywordTypes: undefined,
      gpsr: undefined
    },
    currentlyLoadedAttributeGroups: [],
    currentAttributeGroupPath: undefined,
    attributes: undefined,
    attributeData: {
      attributes: {
        attributeId: undefined,
        values: [],
        units: [],
        deletedUnits: [],
        deletedValues: [],
        modified: false,
        defaultValueOnly: false,
        isMultiSelect: false,
        defaultUnitOnly: false,
        error: false,
        type: "attributes"
      },
      globalAttributes: {
        attributeId: undefined,
        values: [],
        units: [],
        deletedUnits: [],
        deletedValues: [],
        modified: false,
        defaultValueOnly: false,
        isMultiSelect: false,
        defaultUnitOnly: false,
        error: false,
        type: "globalAttributes"
      },
      erpAttributes: {
        attributeId: undefined,
        values: [],
        units: [],
        deletedUnits: [],
        deletedValues: [],
        modified: false,
        defaultValueOnly: false,
        isMultiSelect: false,
        defaultUnitOnly: false,
        error: false,
        type: "erpAttributes"
      }
    },
    priceAttributes: undefined,
    attributeTypes: undefined,
    attributeGroups: undefined,
    attributeDataTypes: undefined,
    globalAttributes: undefined,
    mediaTypes: undefined,
    keywordTypes: undefined,
    conversionProfiles: undefined,
    mamDetails: undefined,
    articleMediaAssets: undefined,
    catalogGroupMediaAssets: undefined,
    defaultCatalogAttributes: undefined,
    printCatalogAttributes: undefined,
    catalogGroupAttributes: undefined,
    referenceTypes: undefined,
    referenceAttributes: undefined,
    erpSections: undefined,
    erpAttributes: undefined,
    localizedBooleanValues: undefined,
    gpsr: {
      links: [],
      currentHash: null
    }
  };
}

export default {
  namespaced: true,
  state: { ...defaultState() },
  getters: {
    isSaving(state) {
      return state.isSaving;
    },
    hasUnsavedData(state) {
      return (
        _.some(state.modified, (x) => x) ||
        state.attributeData.attributes.modified ||
        state.attributeData.globalAttributes.modified ||
        state.attributeData.erpAttributes.modified
      );
    },

    lastRunSuccess(state) {
      return (
        state.metadata.update.success &&
        !state.metadata.update.apiError &&
        !state.attributeData.attributes.error &&
        !state.attributeData.globalAttributes.error &&
        !state.attributeData.erpAttributes.error
      );
    },
    languagesForEditorServices(state, getters, rootState) {
      const enabledLanguages = rootState.metadata.contentLanguages.enabled;
      let languages = enabledLanguages
        .map((x) => [x.languageCode, x.fallbackLanguageCode])
        .flat()
        .filter((x) => !!x);
      languages = Array.from(new Set(languages));
      return languages;
    },
    hasUnSavedDefaultAttributeData(state) {
      return state.attributeData.attributes.modified || state.attributeData.globalAttributes.modified || state.attributeData.erpAttributes.modified;
    }
  },
  mutations: {
    "reset-state"(state) {
      Object.assign(state, defaultState());
    },
    setIsSaving(state, savingState) {
      state.isSaving = savingState;
    },
    resetDirtyState(state, sections) {
      _.forEach(sections || Object.keys(state.modified), (modState) => {
        state.modified[modState] = false;
      });
      state.modified = { ...state.modified };
    },
    markModified(state, tabName) {
      state.modified[tabName] = true;
    },
    setNotificationSeen(state, setValue) {
      state.metadata.update.seen = setValue;
    },
    setPriceAttributes(state, args) {
      state.priceAttributes = args;
    },
    setAttributes(state, args) {
      state.attributes = args;
    },
    overwriteAttributes(state, args) {
      state.attributes = args;
      state.modified.attributes = false;
    },
    updateAttributeIds(state, { languages, newIds, currentAttributeGroupId }) {
      Object.keys(newIds).forEach((index) => {
        const item = state.attributes.filter((x) => x[languages[0]].attributeGroupId == currentAttributeGroupId)[index];
        if (item) {
          languages.forEach((lang) => {
            item[lang].attributeId = newIds[index];
          });
        }
      });
    },
    setAttributeTypes(state, args) {
      state.attributeTypes = args;
    },
    setAttributeGroups(state, args) {
      state.attributeGroups = args;
    },
    setAttributeDataTypes(state, args) {
      state.attributeDataTypes = args;
    },
    setCurrentAttributeGroupPath(state, path) {
      state.currentAttributeGroupPath = path;
    },
    setGlobalAttributes(state, args) {
      state.globalAttributes = args;
    },
    updateGlobalAttributeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.globalAttributes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].globalAttributeId = newIds[index]));
        }
      });
    },
    setMediaTypes(state, args) {
      state.mediaTypes = args;
    },
    updateMediaTypeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.mediaTypes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].mediaAssetTypeId = newIds[index]));
        }
      });
    },
    setKeywordTypes(state, args) {
      state.keywordTypes = args;
    },
    updateKeywordTypeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.keywordTypes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].keywordTypeId = newIds[index]));
        }
      });
    },
    setConversionProfiles(state, args) {
      state.conversionProfiles = args;
    },
    setMamDetails(state, args) {
      state.mamDetails = args;
    },
    setArticleMediaAssets(state, args) {
      state.articleMediaAssets = args;
    },
    updateArticleMediaAssetIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.articleMediaAssets[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].mediaAssetId = newIds[index]));
        }
      });
    },
    updateAttributeTypeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.attributeTypes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].attributeTypeId = newIds[index]));
        }
      });
    },
    setCatalogGroupMediaAssets(state, args) {
      state.catalogGroupMediaAssets = args;
    },
    updateCatalogGroupMediaAssetIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.catalogGroupMediaAssets[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].mediaAssetId = newIds[index]));
        }
      });
    },
    setDefaultCatalogAttributes(state, args) {
      state.defaultCatalogAttributes = args;
    },
    updateDefaultCatalogAttributeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.defaultCatalogAttributes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].catalogAttributeId = newIds[index]));
        }
      });
    },
    setPrintCatalogAttributes(state, args) {
      state.printCatalogAttributes = args;
    },
    updatePrintCatalogAttributeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.printCatalogAttributes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].catalogAttributeId = newIds[index]));
        }
      });
    },
    setCatalogGroupAttributes(state, args) {
      state.catalogGroupAttributes = args;
    },
    updateCatalogGroupAttributeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.catalogGroupAttributes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].catalogNodeAttributeId = newIds[index]));
        }
      });
    },
    setReferenceTypes(state, args) {
      state.referenceTypes = args;
    },
    updateReferenceTypeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.referenceTypes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].id = newIds[index]));
        }
      });
    },
    setReferenceAttributes(state, args) {
      state.referenceAttributes = args;
    },
    updateReferenceAttributeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.referenceAttributes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].referenceAttributeId = newIds[index]));
        }
      });
    },
    setErpSections(state, args) {
      state.erpSections = args;
    },
    updateErpSectionIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.erpSections[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].erpSectionId = newIds[index]));
        }
      });
    },
    setErpAttributes(state, args) {
      state.erpAttributes = args;
    },
    updateErpAttributeIds(state, newIds) {
      Object.keys(newIds).forEach((index) => {
        const item = state.erpAttributes[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].erpAttributeId = newIds[index]));
        }
      });
    },
    setSuccess(state) {
      const updateObject = state.metadata.update;
      updateObject.error = "";
      updateObject.apiError = false;
      updateObject.success = true;
    },
    setError(state, errors) {
      const updateObject = state.metadata.update;
      updateObject.error = errors;
      updateObject.apiError = true;
      updateObject.success = false;
    },
    setSectionError(state, args) {
      const errors = Object.assign({}, state.errors);
      errors[args.section] = args.error;
      state.errors = errors;
    },
    setAttributeDataError: function (state, args) {
      state.attributeData[args.type].error = args.hasError;
    },
    clearDeleteState(state, args) {
      const deleteState = Object.assign({}, state.deleted);
      deleteState[args.section] = [];
      state.deleted = deleteState;
    },
    resetJobsAndErrors(state) {
      state.metadata.update.jobs = [];
      _.forEach(Object.keys(state.errors), (modState) => {
        state.errors[modState] = undefined;
      });
      state.metadata.update.success = false;
    },
    setAttributeData(state, args) {
      const data = { ...args, modified: false, error: false };
      state.attributeData[args.type] = data;
    },
    updateAttributeData(state, args) {
      state.attributeData[args.type] = args.data;
    },
    updateAttributeValueIds: function (state, args) {
      Object.keys(args.newIds).forEach((index) => {
        const item = state.attributeData[args.type].values[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].valueId = args.newIds[index]));
        }
      });
    },
    updateAttributeUnitIds: function (state, args) {
      Object.keys(args.newIds).forEach((index) => {
        const item = state.attributeData[args.type].units[index];
        if (item) {
          Object.keys(item).forEach((lang) => (item[lang].unitId = args.newIds[index]));
        }
      });
    },
    setAttributeDataModified(state, args) {
      state.attributeData[args.type].modified = args.modified;
    },
    resetSections(state) {
      Object.assign(state, defaultState());

      state.deleted = Object.fromEntries(Object.keys(state.deleted).map((x) => [x, []])); //reset deleted entries
    },
    markAsDeleted(state, args) {
      if (Array.isArray(args.value)) {
        const items = state.deleted[args.section];
        items.push(...args.value);
      } else {
        state.deleted[args.section].push(args.value);
      }
    },
    setCurrentlyLoadedAttributeGroups(state, args) {
      state.currentlyLoadedAttributeGroups = args;
    },
    setLocalizedBooleanValues(state, args) {
      state.localizedBooleanValues = args;
    },
    setGPSRLinks(state, args) {
      state.gpsr.links = args;
    },
    setGPSRHash(state, args) {
      state.gpsr.currentHash = args;
    }
  },
  actions: {
    resetAll({ commit }) {
      commit("resetSections");
      commit("resetJobsAndErrors");
      commit("resetDirtyState");
      commit("setIsSaving", false);
    },
    setNotificationSeen({ commit }) {
      commit("setNotificationSeen", true);
    },
    fetchAllData: async function ({ state, commit, dispatch }) {
      commit("resetJobsAndErrors");
      const jobs = state.metadata.update.jobs;
      jobs.push(dispatch("fetchPriceAttributes"));
      await Promise.all(jobs);
    },
    fetchAttributes: async function ({ commit, getters }, { attributeGroupId, valueInherits = true }) {
      const languages = getters.languagesForEditorServices;
      const attributes = await attributesApi.get(languages, attributeGroupId, valueInherits);
      commit("overwriteAttributes", attributes);
    },
    fetchPriceAttributes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;
      const priceAttributes = {};
      let count;
      const tasks = languages.map(async (lang, index) => {
        priceAttributes[lang] = await priceAttributesApi.getPriceAttributes(lang);
        if (index === 0) {
          count = priceAttributes[lang].length;
        }
      });
      await Promise.all(tasks);

      const entries = [];
      for (let index = 0; index < count; index++) {
        const row = languages.reduce((r, lang) => {
          r[lang] = priceAttributes[lang][index];
          return r;
        }, {});

        entries.push(row);
      }

      commit("setPriceAttributes", entries);
    },

    fetchAttributeTypes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const attributeTypes = await attributeTypesApi.get(languages);
        commit("setAttributeTypes", attributeTypes);
      } catch (error) {
        commit("");
      }
    },
    fetchAttributeDataTypes: async function ({ commit }) {
      const datatypes = await attributeDataTypesApi.getAttributeDataTypes();
      commit("setAttributeDataTypes", datatypes);
    },

    fetchGlobalAttributes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const globalAttributes = await globalAttributesApi.get(languages);
        commit("setGlobalAttributes", globalAttributes);
      } catch (error) {
        console.error("global attributes failed to load", error);
      }
    },
    fetchMediaTypes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const mediaTypes = await mediaAssetTypesApi.get(languages);
        commit("setMediaTypes", mediaTypes);
      } catch (error) {
        console.error("media asset types failed to load", error);
      }
    },
    fetchKeywordTypes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const keywordTypes = await keywordTypesApi.get(languages);
        commit("setKeywordTypes", keywordTypes);
      } catch (error) {
        console.error("keyword types failed to load", error);
      }
    },
    fetchLocalizedBooleanValues: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;
      const result = await commonApi.getLocalizedBooleanValues(languages);
      commit("setLocalizedBooleanValues", result);
    },
    async saveKeywordTypes({ state, commit, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.keywordTypes.map((x) => _.pick(x, enabledLanguages)); //pick specified properties and build new object for each item
        const result = await keywordTypesApi.update(strippedModels);

        if (result.newItemIds) {
          commit("updateKeywordTypeIds", result.newItemIds);
        }

        if (state.deleted.keywordTypes.length > 0) {
          await keywordTypesApi.delete(state.deleted.keywordTypes);
          commit("clearDeleteState", { section: "keywordTypes" });
        }
      } catch (error) {
        commit("setSectionError", { section: "keywordTypes", error: error });
      }
    },

    fetchConversionProfiles: async function ({ commit }) {
      try {
        const conversionProfiles = await mediaAssetsApi.getConversionProfiles();
        commit("setConversionProfiles", conversionProfiles);
      } catch (error) {
        console.error("media asset types failed to load", error);
      }
    },
    fetchMamDetails: async function ({ commit }) {
      try {
        const mamInfo = await mediaAssetsApi.getMamDetails();
        commit("setMamDetails", mamInfo);
      } catch (error) {
        console.error("Mam details failed to load", error);
      }
    },
    fetchArticleMediaAssets: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;
      const targetType = "Article";

      try {
        const articleMediaAssets = await mediaAssetsApi.get(languages, targetType);
        commit("setArticleMediaAssets", articleMediaAssets);
      } catch (error) {
        console.error("article media assets failed to load", error);
      }
    },

    fetchCatalogGroupMediaAssets: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;
      const targetType = "CatalogGroup";

      try {
        const catalogGroupMediaAssets = await mediaAssetsApi.get(languages, targetType);
        commit("setCatalogGroupMediaAssets", catalogGroupMediaAssets);
      } catch (error) {
        console.error("Catalog group media assets failed to load", error);
      }
    },
    fetchDefaultCatalogAttributes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const defaultAttributes = await catalogAttributesApi.get(languages, 1);
        commit("setDefaultCatalogAttributes", defaultAttributes);
      } catch (error) {
        console.error("default catalog attributes failed to load", error);
      }
    },
    fetchPrintCatalogAttributes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const printAttributes = await catalogAttributesApi.get(languages, 2);
        commit("setPrintCatalogAttributes", printAttributes);
      } catch (error) {
        console.error("print catalog attributes failed to load", error);
      }
    },
    fetchCatalogGroupAttributes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const attributes = await catalogGroupAttributesApi.get(languages);
        commit("setCatalogGroupAttributes", attributes);
      } catch (error) {
        console.error("catalog group attributes failed to load", error);
      }
    },
    fetchReferenceTypes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const referenceTypes = await referenceTypesApi.getReferenceTypes(languages);
        commit("setReferenceTypes", referenceTypes);
      } catch (error) {
        console.error("reference types failed to load", error);
      }
    },
    fetchReferenceAttributes: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const referenceAttributes = await referenceAttributesApi.get(languages);
        commit("setReferenceAttributes", referenceAttributes);
      } catch (error) {
        console.error("reference attributes failed to load", error);
      }
    },
    fetchErpSections: async function ({ commit, getters }) {
      const languages = getters.languagesForEditorServices;

      try {
        const erpSections = await erpSectionsApi.get(languages);
        commit("setErpSections", erpSections);
      } catch (error) {
        console.error("erp sections failed to load", error);
      }
    },
    fetchErpAttributes: async function ({ commit, getters }, erpSectionId) {
      const languages = getters.languagesForEditorServices;

      try {
        const erpAttributes = await erpAttributesApi.get(languages, erpSectionId);
        commit("setErpAttributes", erpAttributes);
      } catch (error) {
        console.error("erp attributes failed to load", error);
      }
    },
    setPriceAttributes({ commit }, args) {
      commit("setPriceAttributes", args);
      commit("markModified", "priceAttributes");
    },
    selectAttributeGroupPath: async function ({ commit }, path) {
      commit("setCurrentAttributeGroupPath", path);
    },
    saveAllData: async function ({ commit, dispatch, state, getters }) {
      const jobs = state.metadata.update.jobs;
      try {
        if (getters.hasUnsavedData) {
          commit("setIsSaving", true);
          commit("setNotificationSeen", false);
          commit("resetJobsAndErrors");

          if (state.modified.attributes) {
            jobs.push(dispatch("saveAttributes"));
          }
          if (state.modified.priceAttributes) {
            jobs.push(dispatch("savePriceAttributes"));
          }
          if (state.modified.globalAttributes) {
            jobs.push(dispatch("saveGlobalAttributes"));
          }
          if (state.modified.keywordTypes) {
            jobs.push(dispatch("saveKeywordTypes"));
          }
          if (state.modified.attributeTypes) {
            jobs.push(dispatch("saveAttributeTypes"));
          }
          if (state.modified.articleMediaAssets) {
            jobs.push(dispatch("saveArticleMediaAssets"));
          }
          if (state.modified.catalogGroupMediaAssets) {
            jobs.push(dispatch("saveCatalogGroupMediaAssets"));
          }
          if (state.modified.mediaTypes) {
            jobs.push(dispatch("saveMediaTypes"));
          }
          if (state.modified.defaultCatalogAttributes) {
            jobs.push(dispatch("saveDefaultCatalogAttributes"));
          }
          if (state.modified.printCatalogAttributes) {
            jobs.push(dispatch("savePrintCatalogAttributes"));
          }
          if (state.modified.catalogGroupAttributes) {
            jobs.push(dispatch("saveCatalogGroupAttributes"));
          }
          if (state.modified.referenceTypes) {
            jobs.push(dispatch("saveReferenceTypes"));
          }
          if (state.modified.referenceAttributes) {
            jobs.push(dispatch("saveReferenceAttributes"));
          }
          if (state.modified.erpSections) {
            jobs.push(dispatch("saveErpSections"));
          }
          if (state.modified.erpAttributes) {
            jobs.push(dispatch("saveErpAttributes"));
          }
          if (state.modified.gpsr) {
            jobs.push(dispatch("saveGPSR"));
          }
          await Promise.all(jobs);

          if (state.attributeData.attributes.modified || state.attributeData.erpAttributes.modified || state.attributeData.globalAttributes.modified) {
            await dispatch("saveAttributeData");
          }

          if (_.some(state.errors, (x) => !!x)) {
            const errors = Object.values(state.errors).filter((x) => !!x);
            commit("setError", errors);
            throw new Error("During saving an Error occured");
          }
          commit("resetDirtyState");
          commit("setSuccess");
        }
      } catch (error) {
        throw new Error("During saving an Error occured");
      } finally {
        commit("setIsSaving", false);
      }
    },
    saveAttributes: async function ({ commit, state, rootState }) {
      try {
        if (state.deleted.attributes.length) {
          await attributesApi.delete(state.deleted.attributes);
          commit("clearDeleteState", { section: "attributes" });
        }
        const languagesToSave = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        let strippedModels = state.attributes.map((x) => _.pick(x, languagesToSave)); //pick specified properties and build new object for each item
        strippedModels = _.filter(
          strippedModels,
          (x) => x[languagesToSave[0]].attributeGroupId === rootState.userSettings.AttributeManagerSelectedAttributeGroupId
        );
        const result = await attributesApi.update(strippedModels);

        if (result.newItemIds) {
          commit("updateAttributeIds", {
            languages: languagesToSave,
            newIds: result.newItemIds,
            currentAttributeGroupId: rootState.userSettings.AttributeManagerSelectedAttributeGroupId
          });
        }
      } catch (error) {
        commit("setSectionError", { section: "attributes", error: error });
      }
    },
    savePriceAttributes: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const payload = [];
        _.each(enabledLanguages, (lang) => {
          payload.push({
            language: lang,
            priceAttributes: state.priceAttributes.map((x) => x[lang])
          });
        });
        await priceAttributesApi.updatePriceAttributes(payload);
      } catch (error) {
        commit("setSectionError", { section: "priceAttributes", error: error });
      }
    },
    saveAttributeTypes: async function ({ commit, state, rootState }) {
      try {
        const languagesToSave = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.attributeTypes.map((x) => _.pick(x, languagesToSave));
        if (strippedModels.length) {
          const result = await attributeTypesApi.update(strippedModels);
          if (result.newItemIds) {
            commit("updateAttributeTypeIds", result.newItemIds);
          }
        }
        if (state.deleted.attributeTypes.length) {
          await attributeTypesApi.delete(state.deleted.attributeTypes);
        }
      } catch (error) {
        commit("setSectionError", { section: "attributeTypes", error: error });
      }
    },
    saveGlobalAttributes: async function ({ commit, state, rootState }) {
      try {
        const languagesToSave = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.globalAttributes.map((x) => _.pick(x, languagesToSave)); //pick specified properties and build new object for each item
        const result = await globalAttributesApi.update(strippedModels);
        if (result.newItemIds) {
          commit("updateGlobalAttributeIds", result.newItemIds);
        }
        if (state.deleted.globalAttributes.length) {
          await globalAttributesApi.delete(state.deleted.globalAttributes);
        }
      } catch (error) {
        commit("setSectionError", {
          section: "globalAttributes",
          error: error
        });
      }
    },
    saveMediaTypes: async function ({ commit, state, rootState }) {
      try {
        const languagesToSave = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.mediaTypes.map((x) => _.pick(x, languagesToSave)); //pick specified properties and build new object for each item
        const result = await mediaAssetTypesApi.update(strippedModels);

        if (result.newItemIds) {
          commit("updateMediaTypeIds", result.newItemIds);
        }

        if (state.deleted.mediaTypes.length) {
          await mediaAssetTypesApi.delete(state.deleted.mediaTypes);
        }
      } catch (error) {
        commit("setSectionError", { section: "mediaTypes", error: error });
      }
    },
    saveArticleMediaAssets: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.articleMediaAssets.map((x) => _.pick(x, enabledLanguages)); //pick specified properties and build new object for each item
        const targetType = "Article";
        if (state.deleted.articleMediaAssets.length > 0) {
          await mediaAssetsApi.delete(state.deleted.articleMediaAssets);
          commit("clearDeleteState", { section: "articleMediaAssets" });
        }
        if (strippedModels.length) {
          if (state.deleted.mediaTypes.length > 0) {
            const deletedMediaTypes = new Set(state.deleted.mediaTypes);

            for (let i = 0; i < strippedModels.length; i++) {
              const item = strippedModels[i];
              for (const lang in item) {
                const data = item[lang];
                if (data && deletedMediaTypes.has(data.mediaAssetTypeId)) {
                  data.mediaAssetTypeId = null;
                }
              }
            }
          }
          const result = await mediaAssetsApi.update(strippedModels, targetType);

          if (result.newItemIds) {
            commit("updateArticleMediaAssetIds", result.newItemIds);
          }
        }
      } catch (error) {
        commit("setSectionError", {
          section: "articleMediaAssets",
          error: error
        });
      }
    },
    saveCatalogGroupMediaAssets: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.catalogGroupMediaAssets.map((x) => _.pick(x, enabledLanguages)); //pick specified properties and build new object for each item
        const targetType = "CatalogGroup";
        if (state.deleted.catalogGroupMediaAssets.length > 0) {
          await mediaAssetsApi.delete(state.deleted.catalogGroupMediaAssets);
          commit("clearDeleteState", { section: "catalogGroupMediaAssets" });
        }

        if (strippedModels.length) {
          if (state.deleted.mediaTypes.length > 0) {
            const deletedMediaTypes = new Set(state.deleted.mediaTypes);

            for (let i = 0; i < strippedModels.length; i++) {
              const item = strippedModels[i];
              for (const lang in item) {
                const data = item[lang];
                if (data && deletedMediaTypes.has(data.mediaAssetTypeId)) {
                  data.mediaAssetTypeId = null;
                }
              }
            }
          }
          const result = await mediaAssetsApi.update(strippedModels, targetType);

          if (result.newItemIds) {
            commit("updateCatalogGroupMediaAssetIds", result.newItemIds);
          }
        }
      } catch (error) {
        commit("setSectionError", {
          section: "catalogGroupMediaAssets",
          error: error
        });
      }
    },
    saveDefaultCatalogAttributes: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.defaultCatalogAttributes.map((x) => _.pick(x, enabledLanguages));

        if (state.deleted.defaultCatalogAttributes.length) {
          await catalogAttributesApi.delete(state.deleted.defaultCatalogAttributes);
          commit("clearDeleteState", { section: "defaultCatalogAttributes" });
        }

        if (strippedModels.length) {
          const result = await catalogAttributesApi.update(strippedModels);
          if (result.newItemIds) {
            commit("updateDefaultCatalogAttributeIds", result.newItemIds);
          }
        }
      } catch (error) {
        commit("setSectionError", {
          section: "defaultCatalogAttributes",
          error: error
        });
      }
    },
    savePrintCatalogAttributes: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.printCatalogAttributes.map((x) => _.pick(x, enabledLanguages));

        if (state.deleted.printCatalogAttributes.length) {
          await catalogAttributesApi.delete(state.deleted.printCatalogAttributes);
          commit("clearDeleteState", { section: "printCatalogAttributes" });
        }

        if (strippedModels.length) {
          const result = await catalogAttributesApi.update(strippedModels);
          if (result.newItemIds) {
            commit("updatePrintCatalogAttributeIds", result.newItemIds);
          }
        }
      } catch (error) {
        commit("setSectionError", {
          section: "printCatalogAttributes",
          error: error
        });
      }
    },
    saveCatalogGroupAttributes: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.catalogGroupAttributes.map((x) => _.pick(x, enabledLanguages));

        if (state.deleted.catalogGroupAttributes.length) {
          await catalogGroupAttributesApi.delete(state.deleted.catalogGroupAttributes);
          commit("clearDeleteState", { section: "catalogGroupAttributes" });
        }

        if (strippedModels.length) {
          const result = await catalogGroupAttributesApi.update(strippedModels);
          if (result.newItemIds) {
            commit("updateCatalogGroupAttributeIds", result.newItemIds);
          }
        }
      } catch (error) {
        commit("setSectionError", {
          section: "catalogGroupAttributes",
          error: error
        });
      }
    },
    saveReferenceTypes: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.referenceTypes.map((x) => _.pick(x, enabledLanguages));

        if (state.deleted.referenceTypes.length) {
          await referenceTypesApi.deleteReferenceTypes(state.deleted.referenceTypes);
          commit("clearDeleteState", { section: "referenceTypes" });
        }

        if (strippedModels.length) {
          const result = await referenceTypesApi.updateReferenceTypes(strippedModels);
          if (result.newItemIds) {
            commit("updateReferenceTypeIds", result.newItemIds);
          }
        }
      } catch (error) {
        commit("setSectionError", { section: "referenceTypes", error: error });
      }
    },
    saveReferenceAttributes: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.referenceAttributes.map((x) => _.pick(x, enabledLanguages));

        if (state.deleted.referenceAttributes.length) {
          await referenceAttributesApi.delete(state.deleted.referenceAttributes);
          commit("clearDeleteState", { section: "referenceAttributes" });
        }

        if (strippedModels.length) {
          const result = await referenceAttributesApi.update(strippedModels);
          if (result.newItemIds) {
            commit("updateReferenceAttributeIds", result.newItemIds);
          }
        }
      } catch (error) {
        commit("setSectionError", {
          section: "referenceAttributes",
          error: error
        });
      }
    },
    saveErpSections: async function ({ commit, state, rootState }) {
      try {
        const enabledLanguages = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.erpSections.map((x) => _.pick(x, enabledLanguages));

        if (state.deleted.erpSections.length) {
          await erpSectionsApi.delete(state.deleted.erpSections);
          commit("clearDeleteState", { section: "erpSections" });
        }

        const result = await erpSectionsApi.update(strippedModels);
        if (result.newItemIds) {
          commit("updateErpSectionIds", result.newItemIds);
        }
      } catch (error) {
        commit("setSectionError", { section: "erpSections", error: error });
      }
    },
    saveErpAttributes: async function ({ commit, state, rootState }) {
      try {
        const languagesToSave = rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
        const strippedModels = state.erpAttributes.map((x) => _.pick(x, languagesToSave)); //pick specified properties and build new object for each item

        if (state.deleted.erpAttributes.length) {
          await erpAttributesApi.delete(state.deleted.erpAttributes);
          commit("clearDeleteState", { section: "erpAttributes" });
        }

        const result = await erpAttributesApi.update(strippedModels);
        if (result.newItemIds) {
          commit("updateErpAttributeIds", result.newItemIds);
        }
      } catch (error) {
        commit("setSectionError", { section: "erpAttributes", error: error });
      }
    },
    fetchAttributeData: async function ({ getters, commit }, args) {
      const languages = getters.languagesForEditorServices;
      if (args.attributeId && !Guid.isEmpty(args.attributeId)) {
        const attributeValuesModel = await attributeDataApi.getValues(args.type, args.attributeId, languages);
        const attributeUnitsModel = await attributeDataApi.getUnits(args.type, args.attributeId, languages);
        commit("setAttributeData", {
          attributeId: args.attributeId,
          type: args.type,
          units: attributeUnitsModel.defaultAttributeUnits,
          values: attributeValuesModel.defaultAttributeValues,
          defaultValueOnly: attributeValuesModel.defaultValueOnly,
          isMultiSelect: attributeValuesModel.isMultiSelect,
          defaultUnitOnly: attributeUnitsModel.defaultUnitOnly,
          defaultValueShouldSync: attributeValuesModel.shouldSync,
          defaultUnitShouldSync: attributeUnitsModel.shouldSync
        });
      } else {
        const data = {
          attributeId: "",
          values: [],
          units: [],
          defaultValueOnly: false,
          isMultiSelect: false,
          defaultUnitOnly: false,
          defaultValueShouldSync: false,
          defaultUnitShouldSync: false,
          modified: false,
          deletedUnits: [],
          deletedValues: [],
          type: args.type
        };
        commit("setAttributeData", data);
      }
    },
    saveAttributeData: async function (context) {
      const types = Object.keys(context.state.attributeData);
      for (const type of types) {
        const args = context.state.attributeData[type];
        if (args.modified && args.attributeId) {
          try {
            const result = { values: [], units: [] };
            const languagesToSave = context.rootState.metadata.contentLanguages.enabled.map((x) => x.languageCode);
            const strippedValues = args.values.map((x) => _.pick(x, languagesToSave)); //pick specified properties and build new object for each item
            const strippedUnits = args.units.map((x) => _.pick(x, languagesToSave));

            result.values = await attributeDataApi.saveValues(
              args.type,
              strippedValues,
              args.defaultValueOnly,
              args.isMultiSelect,
              args.attributeId,
              args.defaultValueShouldSync
            );
            result.units = await attributeDataApi.saveUnits(args.type, strippedUnits, args.defaultUnitOnly, args.attributeId, args.defaultUnitShouldSync);
            context.commit("setAttributeDataError", { hasError: false, type: type });

            if (args.deletedValues?.length) {
              await attributeDataApi.deleteValues(args.type, args.attributeId, args.deletedValues);
            }

            if (args.deletedUnits?.length) {
              await attributeDataApi.deleteUnits(args.type, args.attributeId, args.deletedUnits);
            }

            if (result.values?.newItemIds) {
              context.commit("updateAttributeValueIds", { newIds: result.values.newItemIds, type: type });
            }
            if (result.units?.newItemIds) {
              context.commit("updateAttributeUnitIds", { newIds: result.units.newItemIds, type: type });
            }
            context.commit("setAttributeDataModified", { modified: false, type: type });
          } catch (error) {
            context.commit("setAttributeDataError", { hasError: true, type: type });
          }
        }
      }
    },
    loadGPSR: async function ({ commit, dispatch, rootState }) {
      try {
        const languageCodes = _.chain(rootState.metadata.contentLanguages.enabled)
          .flatMap((lang) => [lang.languageCode, lang.fallbackLanguageCode])
          .filter((languageCode) => languageCode)
          .uniq()
          .value();
        const links = await gpsrApi.getLinkDefinitions(languageCodes);
        const gpsrHash = hash(links);
        commit("setGPSRHash", gpsrHash);
        dispatch("setGPSRLinks", links);
      } catch (error) {
        commit("setSectionError", { section: "gpsr", error: error });
      }
    },
    setGPSRLinks({ commit, state }, args) {
      commit("setGPSRLinks", args);
      const gpsrHash = hash(state.gpsr.links);
      if (gpsrHash !== state.gpsr.currentHash) {
        commit("markModified", "gpsr");
      }
    },
    saveGPSR: async function ({ commit, state }) {
      try {
        if (Array.isArray(state.deleted.gpsr) && state.deleted.gpsr.length) {
          const linkIdsToDelete = state.deleted.gpsr;
          await gpsrApi.deleteLinkDefinitions(linkIdsToDelete);
        }

        await gpsrApi.saveLinkDefinitions(state.gpsr.links);
        const gpsrHash = hash(state.gpsr.links);
        commit("setGPSRHash", gpsrHash);
      } catch (error) {
        commit("setSectionError", { section: "gpsr", error: error });
      }
    }
  }
};
